/**
 * Shared rules for spoken and typed mental-arithmetic answers.
 * "forty two" and "42" both become 42. Confidence is applied by the quiz runner, not here.
 */
const WORD_VALUES: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

export function normalizeSpokenNumber(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  let text = raw
    .trim()
    .toLowerCase()
    .replace(/,/g, '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;

  let negative = false;
  if (text.startsWith('minus ')) {
    negative = true;
    text = text.slice('minus '.length).trim();
  } else if (text.startsWith('negative ')) {
    negative = true;
    text = text.slice('negative '.length).trim();
  }
  if (!text) return null;

  const direct = parseDecimal(text);
  if (direct != null) {
    return applySign(direct, negative);
  }

  const pointParts = text.split(' point ');
  const whole = parseIntegerWords(pointParts[0].trim());
  if (whole == null) return null;
  let value = whole;
  if (pointParts.length > 1) {
    const digits = parseFractionDigits(pointParts.slice(1).join(' point ').trim());
    if (digits == null) return null;
    value += Number(digits) / 10 ** digits.length;
  } else if (!pointParts[0].trim()) {
    return null;
  }
  return applySign(value, negative);
}

function parseDecimal(text: string): number | null {
  if (!/^[+]?(?:\d+)(?:\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  return Number.isNaN(value) ? null : value;
}

function parseIntegerWords(phrase: string): number | null {
  if (!phrase) return 0;
  if (/^\d+$/.test(phrase)) return Number(phrase);
  let total = 0;
  let current = 0;
  for (const token of phrase.split(' ')) {
    if (!token || token === 'and') continue;
    if (token === 'hundred') {
      current = (current === 0 ? 1 : current) * 100;
      continue;
    }
    if (token === 'thousand') {
      total += (current === 0 ? 1 : current) * 1000;
      current = 0;
      continue;
    }
    const value = WORD_VALUES[token];
    if (value == null) return null;
    current += value;
  }
  return total + current;
}

function parseFractionDigits(phrase: string): string | null {
  if (!phrase) return null;
  let digits = '';
  for (const token of phrase.split(' ')) {
    if (!token) continue;
    if (/^\d$/.test(token)) {
      digits += token;
      continue;
    }
    const value = WORD_VALUES[token];
    if (value == null || value > 9) return null;
    digits += String(value);
  }
  return digits || null;
}

function applySign(value: number, negative: boolean): number {
  return negative ? -value : value;
}
